import { IGridDefinition, IFormDefinition } from "Grids/Interfaces";

import { GridControlTypes, GridTypes, TemplateTypes } from "Enums";

import {
    ButtonControl,
    LinkControl,
    DateTimeControl,
    DateControl,
    TimeControl,
    CheckboxControl,
    HtmlEditorControl,
    NumberControl,
    ImageControl,
    FileUploadControl,
    ComboBoxControl,
    ListControl,
    TextBlockControl,
    TextControl,
    HiddenTextControl,
    HiddenDateControl,
    ContextMenuControl,
    UnsupportedControl,
    MaskedTextBoxControl,
    RtfEditorControl,
    DatabaseImageControl,
    ThreeStateCheckboxControl,
    ColorPickerControl,
    CustomControl,
    MultiSelectControl,
    AutoCompleteControl,
    PeoplePickerControl,
    DateTimeNoTimeZoneControl
} from "Controls";

import { Grid, EasyEdit, Filter, Form, Tree, Dashboard } from "Grids";

import { ControlTypeConfiguration, ControlFactory } from "Configuration/ControlTypeConfiguration";
import { GridTypeConfiguration, GridFactory } from "Configuration/GridTypeConfiguration";
import { GridSettings } from "Configuration/GridSettings";
import { IDashboardDefinition } from "Grids/Interfaces/IDashboardDefinition";

/**
 * ## Configuration
 *
 * The configuration enables you to overwrite settings of controls and grids.
 *
 * You place the changes in the configuration in the CustomConfiguration.js in your Content/Scripts folder.
 * This custom configuration won't be overwritten on future updates.
 * @since 4.0.0
 */
export class Configuration {
    private static controlTypes: Object = {};

    private static gridTypes: Object = {};

    /**
     * The base url for the default dms api endpoint.
     * @since 4.0.0
     */
    public static baseUrl: string = "dms/api/";

    /**
     * Create new GridSettings with default values.
     * @since 4.0.0
     */
    public static createDefaultGridSettings(): GridSettings {
        let gridSettings = new GridSettings();
        let defaultUrlSet = this.baseUrl + "command/set/";
        let defaultUrlGet = this.baseUrl + "command/get/";

        gridSettings.dataUrlSet = defaultUrlSet;
        gridSettings.dataUrlGet = defaultUrlGet;
        return gridSettings;
    }

    /**
     * Initialize all GridTypes, ControlTypes, Templates, etc...
     * @hidden
     */
    public static initialize() {
        this.controlTypes[GridControlTypes.Text] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new TextControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.Number] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new NumberControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.Date] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new DateControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.Checkbox] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new CheckboxControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.List] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new ListControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.Image] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new ImageControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.TextBlock] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new TextBlockControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.HiddenText] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new HiddenTextControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.HiddenDate] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new HiddenDateControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.DateTime] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new DateTimeControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.Time] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new TimeControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.TextTextBlock] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new TextControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.MaskedTextBox] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new MaskedTextBoxControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.ComboBox] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new ComboBoxControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock, serviceProvider.Combobox)
        );
        this.controlTypes[GridControlTypes.HtmlEditor] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new HtmlEditorControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.FileUpload] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new FileUploadControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.ContextMenu] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new ContextMenuControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.RtfEditor] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new RtfEditorControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.SelectDialog] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new UnsupportedControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.DatabaseImage] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new DatabaseImageControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.Edit] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new ButtonControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.Cancel] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new ButtonControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.Add] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new ButtonControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.Update] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new ButtonControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.Delete] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new ButtonControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.Command] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new ButtonControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.Link] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new LinkControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.SelectButton] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new ButtonControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.MultiPageUpdate] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new ButtonControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.ThreeStateCheckbox] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new ThreeStateCheckboxControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.ColorPicker] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new ColorPickerControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.Custom] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new CustomControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );
        this.controlTypes[GridControlTypes.MultiSelect] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new MultiSelectControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock, serviceProvider.Combobox)
        );
        this.controlTypes[GridControlTypes.AutoComplete] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new AutoCompleteControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock, serviceProvider.Combobox)
        );
        this.controlTypes[GridControlTypes.PeoplePicker] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new PeoplePickerControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock, serviceProvider.PeoplePicker)
        );
        this.controlTypes[GridControlTypes.DatasetCommand] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new ButtonControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.DateTimeNoTimeZone] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider) =>
                new DateTimeNoTimeZoneControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock)
        );

        let gridSettings = this.createDefaultGridSettings();
        gridSettings.setTemplate(TemplateTypes.Wrapper, Grid.wrapperTemplate);
        this.gridTypes[GridTypes.AdvancedGrid] = new GridTypeConfiguration(
            (gridDefinition, textBlockCategory, gridSettings, serviceProvider) =>
                new Grid(
                    <IGridDefinition>gridDefinition,
                    textBlockCategory,
                    gridSettings,
                    serviceProvider.Combobox,
                    serviceProvider.Command,
                    serviceProvider.Export,
                    serviceProvider.GridDefinition,
                    serviceProvider.TextBlock
                ),
            gridSettings
        );

        let easyEditGridSettings = this.createDefaultGridSettings();
        easyEditGridSettings.setTemplate(TemplateTypes.Wrapper, EasyEdit.wrapperTemplate);

        this.gridTypes[GridTypes.EasyEdit] = new GridTypeConfiguration(
            (gridDefinition, textBlockCategory, gridSettings, serviceProvider) =>
                new EasyEdit(
                    <IGridDefinition>gridDefinition,
                    textBlockCategory,
                    gridSettings,
                    serviceProvider.Combobox,
                    serviceProvider.Command,
                    serviceProvider.Export,
                    serviceProvider.GridDefinition,
                    serviceProvider.TextBlock
                ),
            easyEditGridSettings
        );

        let formGridSettings = this.createDefaultGridSettings();
        formGridSettings.setTemplate(TemplateTypes.Wrapper, Form.wrapperTemplate);
        formGridSettings.setTemplate(TemplateTypes.Content, Form.formContentTemplate);
        formGridSettings.setTemplate(TemplateTypes.Tab, Form.tabTemplate);
        formGridSettings.setTemplate(TemplateTypes.Group, Form.groupTemplate);
        formGridSettings.setTemplate(TemplateTypes.Columns, Form.columnsTemplate);
        formGridSettings.setTemplate(TemplateTypes.Column, Form.columnTemplate);

        this.gridTypes[GridTypes.Form] = new GridTypeConfiguration(
            (gridDefinition, textBlockCategory, gridSettings, serviceProvider) =>
                new Form(
                    <IFormDefinition>gridDefinition,
                    textBlockCategory,
                    gridSettings,
                    serviceProvider.Combobox,
                    serviceProvider.Command,
                    serviceProvider.GridDefinition,
                    serviceProvider.TextBlock
                ),
            formGridSettings
        );

        let filterGridSettings = this.createDefaultGridSettings();
        filterGridSettings.setTemplate(TemplateTypes.Wrapper, Filter.wrapperTemplate);
        filterGridSettings.setTemplate(TemplateTypes.Content, Form.formContentTemplate);
        filterGridSettings.setTemplate(TemplateTypes.Tab, Form.tabTemplate);
        filterGridSettings.setTemplate(TemplateTypes.Group, Form.groupTemplate);
        filterGridSettings.setTemplate(TemplateTypes.Columns, Form.columnsTemplate);
        filterGridSettings.setTemplate(TemplateTypes.Column, Form.columnTemplate);

        this.gridTypes[GridTypes.Filter] = new GridTypeConfiguration(
            (gridDefinition, textBlockCategory, gridSettings, serviceProvider) =>
                new Filter(
                    <IFormDefinition>gridDefinition,
                    textBlockCategory,
                    gridSettings,
                    serviceProvider.Combobox,
                    serviceProvider.Command,
                    serviceProvider.GridDefinition,
                    serviceProvider.TextBlock
                ),
            filterGridSettings
        );

        let treeSettings = this.createDefaultGridSettings();
        treeSettings.dataUrlGet = this.baseUrl + "command/getHierarchical/";
        treeSettings.setTemplate(TemplateTypes.Wrapper, Tree.wrapperTemplate);
        this.gridTypes[GridTypes.Tree] = new GridTypeConfiguration(
            (gridDefinition, textBlockCategory, gridSettings, serviceProvider) =>
                new Tree(
                    <IGridDefinition>gridDefinition,
                    textBlockCategory,
                    gridSettings,
                    serviceProvider.Combobox,
                    serviceProvider.Command,
                    serviceProvider.GridDefinition,
                    serviceProvider.TextBlock
                ),
            treeSettings
        );

        let dashboardSettings = this.createDefaultGridSettings();
        dashboardSettings.setTemplate(TemplateTypes.Wrapper, Dashboard.wrapperTemplate);
        dashboardSettings.setTemplate(TemplateTypes.Content, Dashboard.contentTemplate);
        dashboardSettings.setTemplate(TemplateTypes.Column, Dashboard.columnTemplate);

        this.gridTypes[GridTypes.Dashboard] = new GridTypeConfiguration(
            (gridDefinition, textBlockCategory, gridSettings, serviceProvider) =>
                new Dashboard(
                    <IDashboardDefinition>gridDefinition,
                    textBlockCategory,
                    gridSettings,
                    serviceProvider.Combobox,
                    serviceProvider.Command,
                    serviceProvider.TextBlock,
                    serviceProvider.GridDefinition
                ),
            dashboardSettings
        );
    }

    /**
     * Get GridTypeConfiguration from internal array.
     * @param gridType
     */
    private static _getGridTypeConfiguration(gridType: GridTypes): GridTypeConfiguration {
        return this.gridTypes[gridType] as GridTypeConfiguration;
    }

    /**
     * Gets the specific type configuration for given GridType.
     * @param gridType GridType.
     * @since 4.0.0
     */
    public static getGridTypeConfiguration(gridType: GridTypes): GridTypeConfiguration {
        return this._getGridTypeConfiguration(gridType);
    }

    /**
     * Gets the factory for GridType.
     * @param gridType GridType
     */
    public static getGridFactory(gridType: GridTypes): GridFactory {
        let gridTypeConfiguration = this._getGridTypeConfiguration(gridType);
        if (gridTypeConfiguration != null) {
            return gridTypeConfiguration.factory;
        }
        return null;
    }

    /**
     * Get ControlTypeConfiguration from internal array.
     * @param controlType
     */
    private static _getControlTypeConfiguration(controlType: GridControlTypes): ControlTypeConfiguration {
        return this.controlTypes[controlType] as ControlTypeConfiguration;
    }

    /**
     * Gets the specific type configuration for GridControlType.
     * @param controlType
     */
    public static getControlTypeConfiguration(controlType: GridControlTypes): ControlTypeConfiguration {
        return this._getControlTypeConfiguration(controlType);
    }

    /**
     * Gets the factory for GridControlType.
     * @param controlType
     */
    public static getControlFactory(controlType: GridControlTypes): ControlFactory {
        let controlTypeConfiguration = this._getControlTypeConfiguration(controlType);
        if (controlTypeConfiguration != null) {
            return controlTypeConfiguration.controlFactory;
        }
        return null;
    }
}

Configuration.initialize();
